/**
 * PR-G — HTML grip overlay layer for the Three.js renderer.
 *
 * Mounted as a sibling `<div>` to the R3F `<Canvas>` inside
 * `Scene3DStage`. Each grip is an absolutely-positioned CSS-pixel `<div>`
 * (size from the GRIPSIZE sysvar, default 5 px) whose CSS
 * `transform: translate(...)` tracks the world → client projection on every
 * camera change. Fill colour per grip state comes from GRIPCOLOR (cold),
 * GRIPHOT (hot — individually warmed grip, deferred to GRIPS-INTERACTION),
 * and GRIPHOVER (cursor-over) — Codex P2 #317.
 *
 * Why HTML instead of GL meshes:
 *   - DOM gives us native pointer-events (mousedown / contextmenu),
 *     keyboard focus, and accessibility for free.
 *   - A fixed pixel box independent of zoom is trivial in CSS, awkward in GL
 *     (would require per-frame uniform updates per grip).
 *   - Existing Playwright e2e specs locate grips via
 *     `[data-testid^="grip-"]` — the SVG stage uses the same testid
 *     contract, so the new overlay drops in with zero test churn.
 *
 * # Architecture (PR-K.1 follow-up)
 *
 * R3F's reconciler crashes with `R3F: Div is not part of THREE
 * namespace!` when it encounters a `ReactDOM.createPortal` returning
 * a DOM tree as a child of `<Canvas>`. The original PR-G overlay
 * triggered exactly this and was disabled in hotfix `b8f7689`.
 *
 * The split:
 *   - `<GripsCameraTracker>` — minimal R3F component mounted **inside**
 *     `<Canvas>`. Uses `useFrame` to push the live camera into the
 *     shared `cameraSnapshot.ts` ref. Returns `null` (renders nothing
 *     in the THREE scene tree).
 *   - `<GripsDomLayer>` — pure React component mounted as a **sibling**
 *     to `<Canvas>`. Reads the camera snapshot, projects each grip
 *     to client pixels, renders the `<div>` tree directly via JSX
 *     (no portal needed — already in the DOM tree, not the R3F tree).
 *
 * Pointer dispatch:
 *   - `mousedown` on a grip → `onGripStart(entityId, gripKind, world)`.
 *     The host component (Scene3DStage) translates that into the
 *     existing `gripDragRef` / `setGripDragWorld` state used by the
 *     SVG stage so the rest of the drag pipeline (mousemove,
 *     mouseup, properties commit) is unchanged.
 *   - Without an `onGripStart` callback the overlay still paints (so
 *     the grip glyph remains visible during draft sessions where the
 *     drag handler is intentionally suppressed).
 *
 * The presentation layer (`GripsLayer`) is independent of both halves
 * and remains directly unit-testable in jsdom without mounting a
 * Canvas — same testing surface as before the split.
 */

import * as React from "react";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useFrame, useThree } from "@react-three/fiber";

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { BlockDefinition, DrawingEntity } from "@dxf-ai-editor/drawing-model";
import type { GripPatchTarget } from "@dxf-ai-editor/cad-core";

import {
  blockDynamicGrips,
  computeGrips,
  type Grip,
  type GripKind,
  type GripShape,
  type GripsMode,
} from "./computeGrips";
import { computeArrayInsertionGrips } from "./arrayGrips";
import { typedEntityGrips } from "./typedEntityGrips";
import {
  getArmedGripSnapshot,
  getHotGripsSnapshot,
  hotGripKey,
  retainHotGripsForEntities,
  setHotGrip,
  subscribeHotGrips,
  type HotGripRef,
} from "./hotGripStore";
import {
  GRIP_ARMED_CURSOR,
  GRIP_ARMED_INDICATOR_COLOR,
  GRIP_ARMED_INDICATOR_PAD_PX,
  GRIP_ARMED_INDICATOR_STROKE_PX,
} from "./gripInteractionConstants";
import {
  getCameraVersion,
  projectWorldToClient,
  setCameraSnapshot,
  subscribeCameraSnapshot,
} from "../cameraSnapshot";
import { applyOffset } from "../coordinateOrigin";
import { isHiddenByLayerChain } from "../layerChainVisibility";
import type { RenderableEntity } from "../types";
import { useSysvarStore } from "../../dynamicInput/sysvarStore";
import { aciToHex } from "../../scene/aciColorMap";

/**
 * GRIPS-SUBSYSTEM (§5.11) — runtime grip glyph theme resolved from the
 * GRIPSIZE / GRIPCOLOR / GRIPHOT / GRIPHOVER sysvars (Codex P2 #317). The
 * overlay must CONSUME these accepted sysvars — a no-op sysvar is a
 * placeholder (CLAUDE.md ПРИОРИТЕТ #1). All values are pre-resolved (ACI →
 * CSS hex) here so the pure presentation layer stays sysvar-agnostic.
 */
export interface GripGlyphTheme {
  /** Glyph box edge in CSS pixels (GRIPSIZE). */
  readonly size: number;
  /** Cold (default-state) grip fill (GRIPCOLOR, resolved). */
  readonly coldColor: string;
  /** Hot grip fill (GRIPHOT, resolved) — individually warmed grip. */
  readonly hotColor: string;
  /** Hover grip fill (GRIPHOVER, resolved). */
  readonly hoverColor: string;
}

/**
 * AutoCAD-parity DEFAULT glyph look — the fallback used when no `theme` prop is
 * passed (unit tests) and the per-slot colour fallback when a GRIP* colour
 * sysvar does not resolve.
 *   - size 5 px — Codex P3 (#317·round-6): the AutoCAD GRIPSIZE DEFAULT is 5,
 *     and GRIPSIZE is the glyph box size in pixels directly (see
 *     {@link gripSizeToPixels}). The prior 8 px came from an 8/5 rescale that
 *     finding P3 removed.
 *   - cold = cyan #00bfff, hot = blue #1860d0 (the prior hardcoded fills)
 *   - hover = the hot colour (sensible default until GRIPHOVER is set)
 */
const DEFAULT_GRIP_THEME: GripGlyphTheme = {
  size: 5,
  coldColor: "#00bfff",
  hotColor: "#1860d0",
  hoverColor: "#1860d0",
};

/**
 * GRIPSIZE → glyph box pixels. Codex P3 (#317·round-6): AutoCAD GRIPSIZE IS
 * the grip-box size in pixels directly (default 5, range 1..255) — not a value
 * that gets rescaled. The prior 8/5 multiplier made the accepted 1..255 sysvar
 * range misleading (SETVAR GRIPSIZE 255 → a 408 px glyph) and meant ordinary
 * values never matched the user's requested size. Use the clamped pixel value
 * verbatim; clamp to the AutoCAD 1..255 domain so an out-of-range SETVAR cannot
 * produce a zero / negative / absurd glyph.
 */
export function gripSizeToPixels(gripSize: number): number {
  // GRIPSIZE is the glyph box edge in pixels — return it directly (clamped).
  return Math.min(255, Math.max(1, Math.round(gripSize)));
}

/**
 * Resolve the live GRIP* sysvar values into a {@link GripGlyphTheme}. ACI
 * indices flow through the repo's one-truth `aciToHex` resolver; a value that
 * does not resolve (ByLayer / ByBlock / out of range) falls back to the
 * default look for that slot so the glyph never disappears.
 */
export function resolveGripTheme(values: {
  GRIPSIZE: number;
  GRIPCOLOR: number;
  GRIPHOT: number;
  GRIPHOVER: number;
}): GripGlyphTheme {
  return {
    size: gripSizeToPixels(values.GRIPSIZE),
    coldColor: aciToHex(values.GRIPCOLOR) ?? DEFAULT_GRIP_THEME.coldColor,
    hotColor: aciToHex(values.GRIPHOT) ?? DEFAULT_GRIP_THEME.hotColor,
    hoverColor: aciToHex(values.GRIPHOVER) ?? DEFAULT_GRIP_THEME.hoverColor,
  };
}

/**
 * Props mirror the slice of `Scene3DStageProps` the overlay needs:
 *   - `entities` + `selectedEntityIds` drive which grips paint.
 *   - `blockDefinitions` flow into `computeGrips` for INSERT entities.
 *   - `onGripStart` is invoked on mousedown; absence = read-only mode.
 */
export interface GripsOverlayProps {
  /** Currently rendered entity feed (model + paper, post-filter). */
  readonly entities: readonly EntityPreview[];
  /** Selection — only selected entities show grips (AutoCAD parity). */
  readonly selectedEntityIds: readonly string[] | undefined;
  /** Block table — drives dynamic-parameter grips for INSERTs. */
  readonly blockDefinitions:
    | Record<string, BlockDefinition>
    | undefined;
  /**
   * Mousedown callback on a grip. Receives the entity id, grip kind,
   * and world-space coordinates. The host component is expected to
   * seed its drag state from these values (matching the SVG stage's
   * `gripDragRef.current = { grip, ... }` path).
   */
  readonly onGripStart?: (
    entityId: string,
    gripKind: GripKind,
    world: { x: number; y: number },
    grip: Grip,
    event: React.PointerEvent<HTMLDivElement>,
  ) => void;
  /**
   * B19 — left-click handler for `block-lookup` grips. Receives the
   * grip + screen-space coordinates so the host can mount a dropdown
   * popup instead of starting a drag. Falls through to `onGripStart`
   * when absent (drag mode = no popup).
   */
  readonly onLookupGripClick?: (
    grip: Grip,
    screen: { x: number; y: number },
  ) => void;
  /**
   * B18 — right-click handler. Receives the grip and screen-space
   * coordinates so the host can mount a context menu. Falls through
   * to the browser default contextmenu when absent.
   */
  readonly onGripContextMenu?: (
    grip: Grip,
    screen: { x: number; y: number },
  ) => void;
  /**
   * Suppress all grip pointer events. Used while a draft session is
   * active so a leftover-selection grip cannot swallow the next
   * pick-point click (mirrors `isDrafting` guard in the SVG stage at
   * `PrimitiveAtlasStage.tsx:2251`).
   */
  readonly suppressed?: boolean;
  /**
   * PR-V0c — set of locked layer names. Selected entities whose layer
   * is in this set do NOT show grips (AutoCAD parity: locked-layer
   * entities are visible but uneditable). Empty / absent = grips for
   * every selected entity.
   */
  readonly lockedLayers?: ReadonlySet<string>;
  /**
   * S12b CADCORE-GRIPS-SELECT-WIRE — the typed-first grip source resolver,
   * INJECTED by the host. Maps a rendered entity id to its canonical
   * `DrawingEntity`; when it resolves, the entity's base grips come from
   * `@dxf-ai-editor/cad-core` (see {@link buildGripSet}). `Scene3DStage`
   * passes `resolveTypedEntityById` (`typedGripResolver.ts`) so the live app
   * sources grips from cad-core; a bare overlay / unit test that omits it
   * falls back to the legacy string-bag grips.
   */
  readonly resolveTypedEntity?:
    | ((id: string) => DrawingEntity | null)
    | null;
  /**
   * Codex PR #784 round 3 P3 — resolves a selected id to its RENDERABLE (the
   * spatial-index entry) so the locked-layer gate can read the entity's
   * block-reference layer chain (`renderParentLayers`): a legacy-branch block
   * child selected under an INSERT on a LOCKED layer shows no grips, exactly
   * as the pick fade / click filter treat it (`layerChainVisibility.ts`).
   * `Scene3DStage` passes the hover spatial index's `resolve`; a bare overlay
   * / unit test that omits it gates on the entity's own layer only.
   */
  readonly resolveRenderable?: GripRenderableResolver | null;
}

/** The renderable fields the grips locked-layer gate reads (see {@link isGripLockedByLayerChain}). */
export type GripRenderableResolver = (
  id: string,
) => Pick<RenderableEntity, "layer" | "renderParentLayers"> | null | undefined;

/**
 * `true` when `entity` may not show grips because its OWN layer or ANY layer
 * of its block-reference chain is locked (Codex PR #784 round 3 P3 — the
 * conjunction `layerChainVisibility.ts` applies on every other locked /
 * hidden path). The own layer is the preview's `Layer` property (the same
 * source `buildGripSet` always read); the chain comes from the resolved
 * renderable when a resolver is supplied — absent either, the gate reduces
 * to the plain own-layer check. An empty `lockedLayers` never locks.
 */
export function isGripLockedByLayerChain(
  entity: Pick<EntityPreview, "id" | "properties">,
  lockedLayers: ReadonlySet<string> | undefined,
  resolveRenderable?: GripRenderableResolver | null,
): boolean {
  if (!lockedLayers || lockedLayers.size === 0) return false;
  const layer = entity.properties.Layer ?? entity.properties.layer ?? "0";
  const chain = resolveRenderable?.(entity.id)?.renderParentLayers;
  return isHiddenByLayerChain(layer, chain, lockedLayers);
}

/** CSS class shared between every grip glyph for layout / hit-testing. */
const GRIP_CLASSNAME = "scene3d-grip";

/**
 * Build the SVG / DOM markup for a single grip glyph. The wrapper
 * `<div>` carries the absolute positioning + the data attributes; the
 * inner SVG renders the actual shape (so triangle / chevron grips
 * paint correctly without resorting to CSS clip-path).
 */
export function renderGripGlyph(
  grip: Grip,
  client: { x: number; y: number },
  isHotGrip: boolean,
  isArmed: boolean,
  isHovered: boolean,
  suppressed: boolean,
  theme: GripGlyphTheme,
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void,
  onContextMenu: (event: React.MouseEvent<HTMLDivElement>) => void,
  onPointerEnter: (event: React.PointerEvent<HTMLDivElement>) => void,
  onPointerLeave: (event: React.PointerEvent<HTMLDivElement>) => void,
  // Codex P2 — whether ANY grip (not necessarily THIS one) is currently
  // click-armed. Every glyph's own inline `cursor` is more specific than the
  // stage shell's `resolveStageCursor` policy, so without this a non-armed
  // grip glyph would keep showing its own `pointer` cursor while hovered
  // during the armed phase -- breaking the modal crosshair exactly over the
  // second-pick targets most likely to be hit (other grips). Defaults to
  // `isArmed` so existing call-sites (unit tests) that omit it keep the
  // pre-fix per-grip-only behaviour.
  anyArmed: boolean = isArmed,
): React.ReactElement {
  // GRIPS-SUBSYSTEM (§5.11) — glyph size from GRIPSIZE (Codex P2 #317).
  const size = theme.size;
  const half = size / 2;

  // Color: AutoCAD parity — fill per grip STATE from the GRIP* sysvars.
  // The ARMED grip (GRIPS-INTERACTION PR-1 — click-armed, modal "** STRETCH **")
  // always paints hot (GRIPHOT) and wins over hover, so it stays visually
  // "picked" even while the cursor moves over it. Otherwise hover (GRIPHOVER)
  // wins over hot (GRIPHOT); else cold (GRIPCOLOR). A grip turns hot only when
  // individually clicked/warmed, NOT because its entity is selected — a
  // selected-entity grip renders cold per AutoCAD.
  const fillColor = isArmed
    ? theme.hotColor
    : isHovered
      ? theme.hoverColor
      : isHotGrip
        ? theme.hotColor
        : theme.coldColor;
  const strokeColor = "#ffffff";

  const innerSvg = renderShape(grip.shape, size, fillColor, strokeColor);

  const rotationDeg = grip.rotationDeg ?? 0;

  return (
    <div
      key={`grip-${grip.entityId}-${grip.id}`}
      data-testid={`grip-${grip.entityId}-${grip.id}`}
      data-entity-id={grip.entityId}
      data-grip-kind={grip.kind}
      data-grip-shape={grip.shape}
      data-grip-id={grip.id}
      data-grip-size={size}
      data-grip-fill={fillColor}
      // GRIPS-INTERACTION PR-1 — armed marker for e2e / visual verification.
      {...(isArmed ? { "data-grip-armed": "true" } : {})}
      className={GRIP_CLASSNAME}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate(${client.x - half}px, ${client.y - half}px) rotate(${rotationDeg}deg)`,
        transformOrigin: `${half}px ${half}px`,
        width: size,
        height: size,
        pointerEvents: suppressed ? "none" : "auto",
        // Any grip armed → the modal "specify stretch point" crosshair on
        // EVERY glyph (Codex P2 — not just the one armed grip itself), so
        // hovering a DIFFERENT grip during the armed phase still reads as
        // the modal second-pick cursor, not that glyph's own idle "pointer".
        cursor: suppressed ? "default" : anyArmed ? GRIP_ARMED_CURSOR : "pointer",
        userSelect: "none",
        zIndex: 5,
        // Armed-grip indicator — a detached ring (CSS outline, so the hit-box
        // is unchanged) marking the click-armed grip distinctly from a merely
        // Shift-warmed hot grip.
        ...(isArmed
          ? {
              outline: `${GRIP_ARMED_INDICATOR_STROKE_PX}px solid ${GRIP_ARMED_INDICATOR_COLOR}`,
              outlineOffset: `${GRIP_ARMED_INDICATOR_PAD_PX}px`,
            }
          : {}),
      }}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {innerSvg}
    </div>
  );
}

/**
 * Render the grip's shape inside its GRIPSIZE-px wrapper. Returns an SVG
 * element so triangle / chevron grips have native vector geometry.
 */
function renderShape(
  shape: GripShape,
  size: number,
  fill: string,
  stroke: string,
): React.ReactElement {
  const half = size / 2;
  // Codex residual R5 (#317) — GRIPSIZE=1 is an ACCEPTED sysvar value
  // (sysvarSchema 1..255) and `gripSizeToPixels` clamps to a minimum of 1, so
  // size=1 reaches here. Several shapes inset their geometry by a FIXED 1 / 3 px
  // (square `size - 1`, chevron apex `size - 1`, alignment `size - 3`), which at
  // size=1 collapses the drawn extent to 0 (or inverts it) → SVG paints NOTHING
  // (the glyph is invisible).
  //
  // FIX: keep the original fixed-inset geometry VERBATIM for size≥2 (so sizes
  // 2..255 render with unchanged AutoCAD-parity proportions) and substitute a
  // proportional, always-positive inset ONLY at the degenerate size=1. `tiny`
  // gates the substitution; the floored values are derived from the box size so
  // the size=1 glyph is a small but VISIBLE shape (non-zero extent).
  const tiny = size < 2;
  // Square edge inset: 0.5 (→ extent size-1) for size≥2; a quarter-box inset at
  // size=1 (→ extent size/2 = 0.5 > 0, visible).
  const sqInset = tiny ? size / 4 : 0.5;
  const sqDrawn = size - 2 * sqInset;
  // Chevron / visibility apex: 1 px above the bottom for size≥2; capped at half
  // the box at size=1 so the apex stays inside with positive height.
  const chevronBottom = tiny ? size - half / 2 : size - 1;
  // Visibility under-line x insets: 1 px for size≥2; an eighth-box at size=1.
  const lineInset = tiny ? size / 8 : 1;

  const common = {
    width: size,
    height: size,
    viewBox: `0 0 ${size} ${size}`,
    style: { display: "block" } as React.CSSProperties,
  };

  switch (shape) {
    case "triangle":
      return (
        <svg {...common}>
          <polygon
            points={`0,0 ${size},${half} 0,${size}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
        </svg>
      );
    case "diamond":
      return (
        <svg {...common}>
          <polygon
            points={`${half},0 ${size},${half} ${half},${size} 0,${half}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
        </svg>
      );
    case "lookup-chevron":
      return (
        <svg {...common}>
          <polygon
            points={`0,0 ${size},0 ${half},${size}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
        </svg>
      );
    case "visibility-chevron":
      return (
        <svg {...common}>
          <polygon
            points={`0,0 ${size},0 ${half},${chevronBottom}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
          <line
            x1={lineInset}
            y1={chevronBottom}
            x2={size - lineInset}
            y2={chevronBottom}
            stroke={stroke}
            strokeWidth={0.5}
          />
        </svg>
      );
    case "alignment": {
      // Skewed parallelogram. Original insets (1 / 3 / size-1 / size-3) are kept
      // VERBATIM for size≥2 (AutoCAD-parity proportions); at size=1 they invert
      // (`size - 3 = -2`), so substitute proportional, ordered insets so a tiny
      // box still draws a positive footprint (residual R5 #317).
      const aEdge = tiny ? size / 8 : 1;
      const aSkew = tiny ? size / 3 : 3;
      const aTop = tiny ? size / 8 : 1;
      const aBottom = size - aTop;
      return (
        <svg {...common}>
          <polygon
            points={`${aEdge},${aBottom} ${size - aSkew},${aBottom} ${size - aEdge},${aTop} ${aSkew},${aTop}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
        </svg>
      );
    }
    case "square":
    default:
      return (
        <svg {...common}>
          <rect
            x={sqInset}
            y={sqInset}
            width={sqDrawn}
            height={sqDrawn}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
        </svg>
      );
  }
}

/**
 * Compute the full grip set for a selection. Pure helper exported for
 * tests + the R3F overlay alike.
 *
 * `lockedLayers` (PR-V0c) — entities whose layer name appears in this
 * set are silently skipped. AutoCAD parity: locked-layer entities
 * remain visible but cannot show grips (they cannot be edited).
 * `resolveRenderable` (Codex PR #784 round 3 P3) extends that gate to the
 * entity's block-reference layer chain — see {@link isGripLockedByLayerChain}.
 *
 * `resolveTypedEntity` (S12b CADCORE-GRIPS-SELECT-WIRE) — the typed-first
 * grip SOURCE. When supplied and it resolves an entity's canonical
 * `DrawingEntity`, that entity's base (entity-shape) grips come from
 * `@dxf-ai-editor/cad-core` via {@link typedEntityGrips} (WCS, no display
 * Y-flip, full MULTILEADER `leaderRoots` hierarchy) instead of the legacy
 * string-bag `computeGrips`. The dynamic-block / MINSERT grips
 * (`blockDynamicGrips`) and the ARRAYRECT insertion chevrons
 * (`computeArrayInsertionGrips`) — both out of cad-core's scope — are
 * layered on top from the SAME one truth both paths use. When the resolver
 * is omitted (unit tests) or an id does not resolve to a typed entity, the
 * legacy `computeGrips` path is used verbatim (zero behaviour change), so
 * this is a pure superset wiring.
 */
export function buildGripSet(
  entities: readonly EntityPreview[],
  selectedEntityIds: readonly string[] | undefined,
  blockDefinitions: Record<string, BlockDefinition> | undefined,
  lockedLayers?: ReadonlySet<string>,
  gripsMode: GripsMode = 2,
  resolveTypedEntity?: (id: string) => DrawingEntity | null,
  resolveRenderable?: GripRenderableResolver | null,
): Grip[] {
  if (!selectedEntityIds || selectedEntityIds.length === 0) return [];
  const selected = new Set(selectedEntityIds);
  const out: Grip[] = [];
  for (const entity of entities) {
    if (!selected.has(entity.id)) continue;
    if (isGripLockedByLayerChain(entity, lockedLayers, resolveRenderable)) continue;
    // ARRAYRECT stays on the LEGACY string-bag grips (Codex P2). Originally
    // (pre-AW-1) this was because cad-core's `arrayRectGrips` placed the
    // count/spacing grips at `axisAngle` in RADIANS for an imported
    // ASSOCIATIVE array, while the live renderer (`arrayRectToInstanced.ts`)
    // + the grip-drag translator (`applyArrayGripDrag`) both read the
    // preview "Axis angle" string as DEGREES unconditionally — a genuine
    // unit mismatch that would have landed cad-core-sourced grips on a
    // DIFFERENT axis than the cells they resize.
    //
    // AW-1's units seam (`dxf-io/handleResolution.ts::
    // axesAngleRadiansToModelDegrees`) + the matching `cad-core/common.ts::
    // rotateByArrayAxisAngle`/`arrayAxisAngleDeg` fix (no longer branches on
    // `associative`) RESOLVE that specific mismatch for the dominant real-
    // AutoCAD (named-value-param) decode path: cad-core and the renderer now
    // agree on DEGREES. This wiring has NOT been switched to the typed
    // cad-core path here — that would be a live-render behaviour change
    // (CLAUDE.md Category C, visual gate required) out of AW-1's scope — but
    // the specific blocking reason recorded above no longer applies; a
    // follow-up PR migrating ARRAYRECT onto the typed grip path should
    // re-verify against a real associative-array fixture rather than
    // re-deriving this reasoning from scratch.
    const typed =
      resolveTypedEntity && entity.type !== "Rectangular Associative Array"
        ? resolveTypedEntity(entity.id)
        : null;
    if (typed) {
      // Typed-first source: cad-core base grips + the string-bag subsystems
      // (dynamic-block / MINSERT) it deliberately excludes, from one truth.
      for (const g of typedEntityGrips(typed, entity.id)) out.push(g);
      for (const g of blockDynamicGrips(entity, blockDefinitions, gripsMode)) {
        out.push(g);
      }
    } else {
      // Legacy string-bag fallback (tests / not-yet-resolvable ids).
      for (const g of computeGrips(entity, blockDefinitions, gripsMode)) {
        out.push(g);
      }
    }
    if (entity.type === "Rectangular Associative Array") {
      const extra = computeArrayInsertionGrips(entity);
      for (const g of extra) out.push(g);
    }
  }
  return out;
}

/**
 * Derive the typed hot-grip reference from a rendered {@link Grip}. The
 * typed `patch` (which `DrawingEntity` field a drag rewrites) is lifted from
 * the cad-core `GripPoint` payload when present (typed-first source); legacy
 * string-bag grips carry no `patch`.
 */
export function gripToHotRef(grip: Grip): HotGripRef {
  const payload = grip.payload;
  const patch =
    payload && typeof payload === "object" && "patch" in payload
      ? ((payload as { patch?: GripPatchTarget }).patch)
      : undefined;
  return {
    entityId: grip.entityId,
    gripId: grip.id,
    kind: grip.kind,
    world: { x: grip.world.x, y: grip.world.y },
    ...(patch !== undefined ? { patch } : {}),
  };
}

/**
 * Pure presentation layer: takes a grip list + projection map and
 * returns the `<div>` tree. No R3F dependencies — unit-testable in
 * jsdom without mounting a Canvas.
 *
 * Used by `GripsDomLayer` (the camera-aware sibling component) and
 * directly by the unit test for the overlay.
 */
export function GripsLayer(props: {
  readonly grips: readonly Grip[];
  readonly projected: ReadonlyMap<string, { x: number; y: number }>;
  readonly selectedEntityIds: readonly string[] | undefined;
  readonly suppressed?: boolean;
  /**
   * GRIPS-SUBSYSTEM (§5.11) — glyph size + per-state fills resolved from the
   * GRIP* sysvars (Codex P2 #317). Defaults to {@link DEFAULT_GRIP_THEME}
   * when omitted (keeps the historical look in unit tests that don't pass it).
   */
  readonly theme?: GripGlyphTheme;
  /**
   * S12b — the typed hot-grip selection model (`hotGripStore.ts`). A grip
   * whose `entityId:gripId` key is present renders HOT (GRIPHOT). Absent /
   * empty (unit tests) = every grip cold, the pre-S12b behaviour.
   */
  readonly hotGrips?: ReadonlyMap<string, HotGripRef>;
  /**
   * GRIPS-INTERACTION PR-1 — the single CLICK-ARMED grip (modal stretch), or
   * `null`/absent. The grip whose `entityId:gripId` matches renders with the
   * armed indicator + hot fill + crosshair cursor.
   */
  readonly armedGrip?: HotGripRef | null;
  /**
   * S12b — invoked on a plain left mousedown on a grip (before `onGripStart`)
   * so the host can warm it in the typed hot-grip model. `additive` carries
   * the Shift modifier (AutoCAD Shift-click adds to the hot set).
   */
  readonly onGripActivate?: (grip: Grip, additive: boolean) => void;
  readonly onGripStart?: GripsOverlayProps["onGripStart"];
  readonly onLookupGripClick?: GripsOverlayProps["onLookupGripClick"];
  readonly onGripContextMenu?: GripsOverlayProps["onGripContextMenu"];
}): React.ReactElement {
  const theme = props.theme ?? DEFAULT_GRIP_THEME;
  // GRIPHOVER state — which grip the cursor is currently over (AutoCAD parity:
  // a hovered grip recolours to GRIPHOVER). `null` = none hovered.
  const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);
  const elements: React.ReactElement[] = [];
  for (const grip of props.grips) {
    // Same `entityId:gripId` key the projection map + the hot-grip store use
    // (S12b — `hotGripKey` keeps all three in lockstep).
    const key = hotGripKey(grip.entityId, grip.id);
    const client = props.projected.get(key);
    if (!client) continue;
    // Entity-selection only gates WHETHER grips show (the `selected` filter
    // that builds the grip set), NOT their colour. The per-grip HOT state
    // (GRIPHOT) comes from the typed hot-grip selection model (S12b
    // `hotGripStore.ts`): a grip the user has warmed (clicked) renders hot,
    // every other selected-entity grip renders cold (GRIPCOLOR) per AutoCAD.
    const isHotGrip = props.hotGrips?.has(key) === true;
    // GRIPS-INTERACTION PR-1 — the click-armed grip (modal stretch). Compared
    // via the SAME `entityId:gripId` key the hot map + projection map use.
    const armed = props.armedGrip ?? null;
    const isArmed =
      armed !== null && hotGripKey(armed.entityId, armed.gripId) === key;
    const isHovered = hoveredKey === key;
    const onPointerEnter = () => {
      if (props.suppressed) return;
      setHoveredKey(key);
    };
    const onPointerLeave = () => {
      setHoveredKey((prev) => (prev === key ? null : prev));
    };
    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      if (props.suppressed) return;
      // B19 — left-click on lookup-grip opens a dropdown popup
      // instead of starting a drag.
      if (
        event.button === 0 &&
        grip.kind === "block-lookup" &&
        props.onLookupGripClick
      ) {
        event.stopPropagation();
        event.preventDefault();
        props.onLookupGripClick(grip, {
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }
      if (event.button !== 0) return;
      event.stopPropagation();
      // S12b — warm the grip in the typed hot-grip selection model BEFORE the
      // drag seed (Shift = additive, AutoCAD parity). The grabbed grip
      // recolours to GRIPHOT for the duration of the interaction.
      props.onGripActivate?.(grip, event.shiftKey);
      props.onGripStart?.(grip.entityId, grip.kind, grip.world, grip, event);
    };
    const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
      if (props.suppressed) return;
      if (!props.onGripContextMenu) return;
      event.preventDefault();
      event.stopPropagation();
      props.onGripContextMenu(grip, {
        x: event.clientX,
        y: event.clientY,
      });
    };
    elements.push(
      renderGripGlyph(
        grip,
        client,
        isHotGrip,
        isArmed,
        isHovered,
        props.suppressed === true,
        theme,
        onPointerDown,
        onContextMenu,
        onPointerEnter,
        onPointerLeave,
        armed !== null,
      ),
    );
  }
  return (
    <div
      data-testid="scene3d-grips-layer"
      className="scene3d-grips-layer"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    >
      {elements}
    </div>
  );
}

/**
 * Build the canonical projected-position key for a grip. Stable across
 * re-renders so React keying / testid alignment work identically in
 * the overlay and its test harness.
 */
export function gripProjectionKey(grip: Grip): string {
  return `${grip.entityId}:${grip.id}`;
}

/**
 * R3F-side camera tracker. Mounts **inside** `<Canvas>`. Pushes the
 * live camera + canvas size into the shared `cameraSnapshot.ts`
 * module so the sibling `<GripsDomLayer>` (and other DOM overlays)
 * can project world → client pixels without ever touching the R3F
 * tree.
 *
 * Returns `null` — the component contributes no THREE objects to the
 * scene. R3F is fine with `null` children in `<Canvas>`.
 *
 * NOTE: Multiple consumers can mount their own camera trackers (one
 * per overlay, e.g. grips + scene-overlay). They all push to the same
 * snapshot; the version counter advances by `N` per frame instead of
 * `1`, but DOM consumers only re-render once because React batches.
 */
export function GripsCameraTracker(): null {
  const { camera, size } = useThree((s) => ({ camera: s.camera, size: s.size }));
  useFrame(() => {
    setCameraSnapshot(camera, size);
  });
  return null;
}

/**
 * React hook: subscribe to the shared camera snapshot version so the
 * component re-renders whenever the camera changes (pan, zoom, resize).
 * Returns the current version number — callers usually ignore the
 * value and rely on the re-render side-effect.
 */
export function useCameraSnapshotVersion(): number {
  return useSyncExternalStore(
    subscribeCameraSnapshot,
    getCameraVersion,
    // SSR fallback — version 0 means "no snapshot yet"; consumers
    // gracefully render an empty layer.
    () => 0,
  );
}

/**
 * Sibling-to-Canvas DOM grip layer. Pure React, never mounts inside
 * `<Canvas>`. Subscribes to the camera snapshot version so it
 * re-renders on pan/zoom; computes per-grip client coords via
 * `projectWorldToClient`; delegates the actual `<div>` tree to
 * `GripsLayer`.
 *
 * Render is cheap because:
 *   - `buildGripSet` is memoised on (entities, selection, blockDefs).
 *   - The projection map is rebuilt every camera tick, but each
 *     grip is one matrix-multiply + viewport-scale (negligible vs.
 *     React reconciliation on the resulting div tree).
 */
/**
 * Phase 1.k (П35Р React #185 fix) — outer gate. When the selection is
 * empty (no grips to project), short-circuit BEFORE mounting
 * `GripsDomLayerInner`. The inner component owns the
 * `useCameraSnapshotVersion()` `useSyncExternalStore` subscription;
 * mounting it while the bulk-load pipeline keeps calling
 * `invalidate()` → `useFrame` → `setCameraSnapshot()` → version-bump
 * left React unable to settle the snapshot between render and
 * commit-mount, tripping forceStoreRerender 50+ times in a row →
 * error #185 on the 685k-entity P35R import.
 *
 * `selectedEntityIds.length === 0` is the standard idle-state on a
 * fresh import; the gate keeps the subscription dormant until the
 * user actually picks something. Once a selection exists, the inner
 * mounts and the camera subscription is back online — but by then the
 * bulk-load storm has settled and there is no scenario where the
 * grip overlay accumulates 50 forced re-renders in a single commit
 * cycle.
 */
export function GripsDomLayer(
  props: GripsOverlayProps,
): React.ReactElement | null {
  // GRIPS-SUBSYSTEM (§5.11) — SETVAR GRIPS 0 suppresses ALL grip glyphs
  // even while an entity is selected (AutoCAD GRIPS sysvar semantics).
  // Hook order is preserved (called unconditionally) before the
  // selection short-circuit.
  const gripsEnabled = useSysvarStore((s) => s.values.GRIPS) !== 0;
  // Prune the typed hot-grip model to the LIVE selection here in the OUTER
  // component (Codex P2). The inner `GripsDomLayerInner` — which used to own
  // this prune — UNMOUNTS the instant the selection becomes empty (the
  // `length === 0` early return below) or GRIPS is set to 0, so an effect that
  // lived only in the inner would never fire for the deselect-ALL transition:
  // a warmed grip would survive and render hot again the next time the same
  // entity is selected. This effect sits BEFORE both early returns (a hook, so
  // hook order stays stable), so it re-runs on EVERY selection change —
  // including the shrink-to-empty one — regardless of whether the inner is
  // currently mounted. Keyed on the selection identity, not per render.
  useEffect(() => {
    retainHotGripsForEntities(new Set(props.selectedEntityIds ?? []));
  }, [props.selectedEntityIds]);
  if (!gripsEnabled) return null;
  if (props.selectedEntityIds.length === 0) return null;
  return <GripsDomLayerInner {...props} />;
}

function GripsDomLayerInner(
  props: GripsOverlayProps,
): React.ReactElement | null {
  // GRIPS-SUBSYSTEM (§5.11) — GRIPS sysvar mode. 0 is handled by the outer
  // gate (GripsDomLayer); here only 1 (object grips, no block grips) and 2
  // (full) are possible. Clamp defensively to those two values.
  const gripsMode: GripsMode = useSysvarStore((s) => s.values.GRIPS) === 1
    ? 1
    : 2;
  // GRIPS-SUBSYSTEM (§5.11) — glyph theme from GRIPSIZE / GRIPCOLOR / GRIPHOT
  // / GRIPHOVER (Codex P2 #317). Subscribed individually so a SETVAR on any
  // of them re-renders the overlay with the new size / colours.
  const gripSize = useSysvarStore((s) => s.values.GRIPSIZE);
  const gripColor = useSysvarStore((s) => s.values.GRIPCOLOR);
  const gripHot = useSysvarStore((s) => s.values.GRIPHOT);
  const gripHover = useSysvarStore((s) => s.values.GRIPHOVER);
  const theme = useMemo<GripGlyphTheme>(
    () =>
      resolveGripTheme({
        GRIPSIZE: gripSize,
        GRIPCOLOR: gripColor,
        GRIPHOT: gripHot,
        GRIPHOVER: gripHover,
      }),
    [gripSize, gripColor, gripHot, gripHover],
  );
  // S12b — the typed-first grip source resolver, INJECTED by the host
  // (`Scene3DStage` passes `resolveTypedEntityById` from the store-coupled
  // `typedGripResolver.ts`). Keeping the default `null` here means this
  // module never imports the editor store — the live cad-core source is wired
  // by the prop, and a bare overlay (unit tests) falls back to legacy grips.
  const resolveTypedEntity = props.resolveTypedEntity ?? null;
  // Codex PR #784 round 3 P3 — the renderable resolver for the locked-layer
  // CHAIN gate, injected the same way (the host binds it to its spatial index).
  const resolveRenderable = props.resolveRenderable ?? null;
  const grips = useMemo<Grip[]>(
    () =>
      buildGripSet(
        props.entities,
        props.selectedEntityIds,
        props.blockDefinitions,
        props.lockedLayers,
        gripsMode,
        resolveTypedEntity ?? undefined,
        resolveRenderable,
      ),
    [
      props.entities,
      props.selectedEntityIds,
      props.blockDefinitions,
      props.lockedLayers,
      gripsMode,
      resolveTypedEntity,
      resolveRenderable,
    ],
  );

  // S12b — the typed HOT-GRIP selection model. Subscribe so a warm / prune
  // re-renders the overlay with the new GRIPHOT colouring.
  const hotGrips = useSyncExternalStore(
    subscribeHotGrips,
    getHotGripsSnapshot,
    getHotGripsSnapshot,
  );
  // GRIPS-INTERACTION PR-1 — the single click-armed grip (modal stretch). Same
  // notification channel as the hot map, so a arm / disarm re-renders here.
  const armedGrip = useSyncExternalStore(
    subscribeHotGrips,
    getArmedGripSnapshot,
    getArmedGripSnapshot,
  );
  // NOTE: the hot-grip prune to the live selection runs in the OUTER
  // `GripsDomLayer` (see its `retainHotGripsForEntities` effect), NOT here —
  // the inner unmounts on deselect-all, so a prune here would miss that exact
  // transition (Codex P2). The inner only READS the pruned snapshot above.
  // Warm the grabbed grip in the typed model (Shift = additive). Suppressed
  // during a draft session (the overlay swallows no grip pointer events then).
  const onGripActivate = useCallback(
    (grip: Grip, additive: boolean) => {
      if (props.suppressed) return;
      setHotGrip(gripToHotRef(grip), additive);
    },
    [props.suppressed],
  );

  // Re-render hook: any camera change increments the version, which
  // causes useSyncExternalStore to schedule a re-render. We include
  // the version in the projection's `useMemo` deps so the projection
  // is only recomputed when either grips change or the camera
  // changed — bare equality check, no matrix-hash needed.
  const cameraVersion = useCameraSnapshotVersion();

  // Project each grip to client pixels. Skip grips that fail
  // projection (snapshot not yet ready on the very first paint).
  const projected = useMemo(() => {
    const m = new Map<string, { x: number; y: number }>();
    for (const grip of grips) {
      // S12b Codex P2 — a grip's `world` is DOCUMENT-frame (raw WCS): BOTH the
      // legacy string-bag source (`computeGrips`, reading the raw preview) AND
      // the typed cad-core source (`typedEntityGrips`, reading `DrawingEntity`
      // fields directly) carry TRUE DXF coordinates — that is the frame the
      // grip-drag COMMIT dispatches (`StageGripDrag` maps the pointer back via
      // `unapplyOffset`) and the typed hot-grip model stores. The camera
      // `projectWorldToClient` reads operates in OFFSET-WORLD (`DXF − locked
      // coordinateOrigin`), like the ~30 render adapters that call `applyOffset`
      // before pushing to the GPU. So a raw grip on a non-zero-origin (geodetic /
      // UTM `setCoordinateOrigin`) import would project ~1e6 off the rendered
      // geometry. Convert to the render frame at this SINGLE projection choke-
      // point so EVERY grip source lands on its entity; `applyOffset` is a no-op
      // when no origin is locked (unit tests / small drawings).
      const off = applyOffset({ x: grip.world.x, y: grip.world.y, z: 0 });
      const client = projectWorldToClient(off.x, off.y);
      if (!client) continue;
      m.set(gripProjectionKey(grip), client);
    }
    return m;
  }, [grips, cameraVersion]);

  // exactOptionalPropertyTypes — only forward optional fields when
  // they are actually defined on the inbound props.
  const layerProps: React.ComponentProps<typeof GripsLayer> = {
    grips,
    projected,
    selectedEntityIds: props.selectedEntityIds,
    theme,
    hotGrips,
    armedGrip,
    onGripActivate,
    ...(props.suppressed !== undefined ? { suppressed: props.suppressed } : {}),
    ...(props.onGripStart !== undefined
      ? { onGripStart: props.onGripStart }
      : {}),
    ...(props.onLookupGripClick !== undefined
      ? { onLookupGripClick: props.onLookupGripClick }
      : {}),
    ...(props.onGripContextMenu !== undefined
      ? { onGripContextMenu: props.onGripContextMenu }
      : {}),
  };
  return <GripsLayer {...layerProps} />;
}

